/**
 * Reading the incident history (Levent, 22 Oct 2026; UI-26).
 *
 * Pure, like monitoringView.js: the page draws what these return and decides nothing itself.
 *
 * An incident is ONE row per problem (backend services/opsIncidents.service.js). While the checks
 * keep finding the problem the same row is updated (lastSeenAt, occurrences); a person can take
 * it (In progress) or close it (Resolved) with a note; and the watch resolves it itself, as
 * 'auto', once the check is healthy again. Resolved rows stay as the history.
 */
import { formatDMYTime } from '../../../utils/dateFormat';

/** The statuses the watch still treats as unresolved (same list as the backend). */
export const ACTIVE_STATUSES = ['Open', 'In progress'];

/** The three views of the history, in the order they are offered. `key` is the API's ?status=. */
export const INCIDENT_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'all', label: 'All' },
];

/** Still something to act on: Open or In progress. Anything else (Resolved, unknown) is not. */
export const isActiveIncident = (incident) => ACTIVE_STATUSES.includes(incident?.status);

const SEVERITY_RANK = { critical: 0, warning: 1 };

/** Lower is more urgent; an unknown severity sorts after the known ones. */
export const severityRank = (severity) => SEVERITY_RANK[severity] ?? 2;

/**
 * Critical first, then warning, then anything else. Within one severity the order given is kept,
 * so the backend's own ordering (newest first, or oldest first for the dashboard) survives.
 */
export const sortBySeverity = (items) =>
  (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item, index) => ({ item, index }))
    .sort((a, b) => severityRank(a.item.severity) - severityRank(b.item.severity) || a.index - b.index)
    .map(({ item }) => item);

export const severityLabel = (severity) => {
  if (severity === 'critical') return 'Critical';
  if (severity === 'warning') return 'Warning';
  return severity ? String(severity) : 'Unknown';
};

/** The part of the platform an incident is about (the backend's `source`), in the page's words. */
const AREAS = {
  cache: 'Search cache',
  hotelbeds: 'Hotelbeds updates',
  flights: 'Airtürk imports',
  packages: 'Package precalculation',
  images: 'Hotel photo sync',
  w2m: 'W2M mappings',
  system: 'System',
};
export const areaLabel = (source) => AREAS[source] || (source ? String(source) : 'Unknown area');

/** "1 check", "12 checks": how many checks in a row found the problem. */
export const occurrencesLabel = (n) => {
  const count = Math.max(1, Math.round(Number(n)) || 1);
  return `${count} check${count === 1 ? '' : 's'}`;
};

/** Who closed it and when, or null while it is still active. 'auto' means the check recovered. */
export const resolutionLabel = (incident) => {
  if (incident?.status !== 'Resolved') return null;
  const who = incident.resolvedBy === 'auto'
    ? 'auto (the check was healthy again)'
    : incident.resolvedBy || 'unknown';
  return incident.resolvedAt ? `${formatDMYTime(incident.resolvedAt)} by ${who}` : `by ${who}`;
};

/** Who took it, for an incident In progress. The backend keeps the last person who changed it. */
export const takenByLabel = (incident) =>
  incident?.status === 'In progress' ? incident.updatedBy || 'unknown' : null;

/** Whether the one alert email went out. Not sent usually means no address or no mail set up. */
export const emailLabel = (incident) =>
  incident?.emailedAt ? `Sent ${formatDMYTime(incident.emailedAt)}` : 'Not sent';

/** What a person may do with an incident: take an Open one, resolve any active one. */
export const availableActions = (incident) => {
  if (incident?.status === 'Open') return ['take', 'resolve'];
  if (incident?.status === 'In progress') return ['resolve'];
  return [];
};

export const INCIDENT_ACTIONS = {
  take: { status: 'In progress', label: 'Take (In progress)', done: 'taken (In progress)', failed: 'Could not take the incident.' },
  resolve: { status: 'Resolved', label: 'Resolve', done: 'resolved', failed: 'Could not resolve the incident.' },
};

/**
 * The PATCH body for an action. The note is optional: an empty note is left out, so a note
 * someone wrote earlier is kept (the backend only replaces the note when one is sent).
 */
export const incidentPatch = (action, note) => {
  const status = INCIDENT_ACTIONS[action]?.status;
  if (!status) return null;
  const text = typeof note === 'string' ? note.trim() : '';
  return text ? { status, note: text } : { status };
};

/** The success toast after an action, naming the incident so two quick clicks stay readable. */
export const actionDoneMessage = (action, incident) =>
  `"${incident?.title || 'Incident'}" ${INCIDENT_ACTIONS[action]?.done || 'updated'}`;

/** One line per tab when the list is empty. */
export const emptyIncidentsText = (tab) => {
  if (tab === 'resolved') return 'No resolved incidents yet.';
  if (tab === 'all') return 'No incidents recorded yet.';
  return 'No active incidents. Nothing the checks found is still open.';
};

/** The pager under the list. `count` is how many rows this page holds. */
export const pageInfo = ({ total = 0, offset = 0, limit = 50, count = 0 } = {}) => {
  const t = Math.max(0, Number(total) || 0);
  const o = Math.max(0, Number(offset) || 0);
  const l = Math.max(1, Number(limit) || 50);
  const c = Math.max(0, Number(count) || 0);
  return {
    from: c ? o + 1 : 0,
    to: c ? Math.min(o + c, t || o + c) : 0,
    total: t,
    hasPrev: o > 0,
    hasNext: o + c < t,
    prevOffset: Math.max(0, o - l),
    nextOffset: o + l,
    show: t > l || o > 0,
  };
};
